import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SmartForm } from '@certemy/ui-components';
import { ProfileCardService } from 'components/profile-card/profile-card.service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ROLE_CODES } from 'constants/role.constants';
import { OrgUserProfile } from 'interfaces/profile.interface';
import _orderBy from 'lodash-es/orderBy';
import { ClientServicesService } from 'services/client-services/client-services.service';

@UntilDestroy()
@Component({
  selector: 'cm-edit-profile-manager',
  templateUrl: './edit-profile-manager.component.html',
  styleUrls: ['./edit-profile-manager.component.scss'],
})
export class EditProfileManagerComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() profile: OrgUserProfile;
  @Input() showError = false;
  @Input() locationsFeatureFlag = false;
  @Input() isGlobalProfile: boolean;
  @Output() setManager = new EventEmitter<string>();
  managers: SmartForm.FieldOption[] = [];
  ROLE_CODES = ROLE_CODES;
  managerPlaceholder: string;
  searchManager$ = new BehaviorSubject<string>('');

  constructor(
    private profileCardService: ProfileCardService,
    private clientServicesService: ClientServicesService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const managerData = this.profile.managerData;

    if (managerData) {
      this.managerPlaceholder = `${managerData.firstName} ${managerData.lastName}`;
    }

    this.form
      .get('managerProfileId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((data) => {
        const manager = this.managers.find((item) => item.id === data);
        this.managerPlaceholder = manager ? manager.name : '';
        this.setManager.emit(this.managerPlaceholder);
      });

    if (this.profile.role_id === ROLE_CODES.PROFESSIONAL && this.locationsFeatureFlag) {
      this.getManagersWithLocations()
        .pipe(untilDestroyed(this))
        .subscribe((managers) => {
          this.managers = managers;
          this.cd.markForCheck();
        });
    }

    if (this.profile.role_id === ROLE_CODES.ORGANIZATION_ADMIN || !this.locationsFeatureFlag) {
      this.searchManager$
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((data) => this.getManagers(data)),
          untilDestroyed(this),
        )
        .subscribe((data) => {
          this.managers = data;
          this.cd.markForCheck();
        });
    }
  }

  getManagers(search: string): Observable<SmartForm.FieldOption[]> {
    if (this.isGlobalProfile) {
      return this.clientServicesService
        .getManagers(this.profile.organization_id, { managedProfileId: this.profile.profile_id }, search)
        .pipe(
          map((options) => {
            const firstNameSorter = (option) => option.firstName.toLowerCase();
            const lastNameSorter = (option) => option.lastName.toLowerCase();
            return _orderBy(options, [firstNameSorter, lastNameSorter], ['asc', 'asc']);
          }),
          map((item) =>
            item.map((user) => ({
              id: user.profileId,
              name: `${user.firstName} ${user.lastName}`,
              htmlMarkup: `<span>${user.firstName} ${user.lastName}</span>`,
              useHtml: true,
              tooltip: `Email: ${user.email}`,
            })),
          ),
        );
    } else {
      return this.profileCardService.getManagers({ managedProfileId: this.profile.profile_id }, search).pipe(
        map((users) =>
          users.map((user) => ({
            id: user.profileId,
            name: `${user.firstName} ${user.lastName}`,
            htmlMarkup: `<span>${user.firstName} ${user.lastName}</span>`,
            useHtml: true,
            tooltip: user.email,
          })),
        ),
        take(1),
      );
    }
  }

  getManagersWithLocations(): Observable<SmartForm.FieldOption[]> {
    const additionalOffice$: Observable<number[]> = this.form.get('office_ids').valueChanges.pipe(
      startWith(this.form.get('office_ids').value as number[]),
      distinctUntilChanged(),
      map((data) => (data ? data : [])),
      untilDestroyed(this),
    );

    const manager$: Observable<string> = this.searchManager$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    if (this.isGlobalProfile) {
      return combineLatest([additionalOffice$, manager$]).pipe(
        switchMap(([additional, search]) => {
          return this.clientServicesService.getManagers(
            this.profile.organization_id,
            { officeIds: [this.form.get('office_id').value, ...additional] },
            search,
          );
        }),
        map((options) => {
          const firstNameSorter = (option) => option.firstName.toLowerCase();
          const lastNameSorter = (option) => option.lastName.toLowerCase();
          return _orderBy(options, [firstNameSorter, lastNameSorter], ['asc', 'asc']);
        }),
        map((item) =>
          item.map((user) => ({
            id: user.profileId,
            name: `${user.firstName} ${user.lastName}`,
            htmlMarkup: `<span>${user.firstName} ${user.lastName}</span>`,
            useHtml: true,
            tooltip: `Email: ${user.email}`,
          })),
        ),
      );
    } else {
      return combineLatest([additionalOffice$, manager$]).pipe(
        switchMap(([additional, search]) => {
          return this.profileCardService.getManagers(
            { officeIds: [this.form.get('office_id').value, ...additional] },
            search,
          );
        }),
        map((users) =>
          users.map((user) => ({
            id: user.profileId,
            name: `${user.firstName} ${user.lastName}`,
            htmlMarkup: `<span>${user.firstName} ${user.lastName}</span>`,
            useHtml: true,
            tooltip: user.email,
          })),
        ),
        untilDestroyed(this),
      );
    }
  }

  setManagersWithSearch(event: string): void {
    if (!event || event.length >= 2) {
      this.searchManager$.next(event);
    }
  }

  ngOnDestroy() {}
}
